import React from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import styles from '../DslAgentTestPage.module.css';

export interface DslExpressionEditorProps {
  request: string;
  onRequestChange: (value: string) => void;
  dsl: string;
  onDslChange: (value: string) => void;
  isGenerating: boolean;
  generateError: string | null;
  onGenerate: () => void;
}

export const DslExpressionEditor: React.FC<DslExpressionEditorProps> = ({
  request,
  onRequestChange,
  dsl,
  onDslChange,
  isGenerating,
  generateError,
  onGenerate,
}) => {
  const canGenerate = request.trim().length > 0 && !isGenerating;

  const handlePromptKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      if (canGenerate) {
        onGenerate();
      }
    }
  };

  return (
    <>
      <div className={styles.section}>
        <label className={styles.sectionLabel} htmlFor="dsl-agent-prompt">
          Natural-language request
        </label>
        <p className={styles.sectionHint}>
          e.g. moving average of pnl of SPY for 2025 with ma window 5 and 1 day pnl
        </p>
        <textarea
          id="dsl-agent-prompt"
          className={styles.promptArea}
          placeholder="Describe the analytics you want…"
          value={request}
          onChange={(event) => onRequestChange(event.target.value)}
          onKeyDown={handlePromptKeyDown}
          disabled={isGenerating}
          aria-label="Natural language DSL request"
          data-testid="dsl-agent-prompt"
        />
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={onGenerate}
          disabled={!canGenerate}
          aria-label="Generate DSL expression"
          title="Generate DSL (Cmd/Ctrl + Enter)"
          data-testid="dsl-agent-generate-button"
        >
          {isGenerating ? (
            <Loader2 size={16} className={styles.spin} aria-hidden="true" />
          ) : (
            <Sparkles size={16} aria-hidden="true" />
          )}
          <span>{isGenerating ? 'Generating…' : 'Generate DSL'}</span>
        </button>
      </div>

      {generateError ? (
        <p className={styles.errorBlock} data-testid="dsl-agent-generate-error" role="alert">
          <span className={styles.errorCategory}>Generation error:</span>
          {generateError}
        </p>
      ) : null}

      <div className={styles.section}>
        <label className={styles.sectionLabel} htmlFor="dsl-agent-dsl">
          DSL expression
        </label>
        <p className={styles.sectionHint}>
          Edit if needed, then submit to execute.
        </p>
        <textarea
          id="dsl-agent-dsl"
          className={styles.dslArea}
          placeholder="ma(stock(SPY), window=5)"
          value={dsl}
          onChange={(event) => onDslChange(event.target.value)}
          spellCheck={false}
          aria-label="DSL expression"
          data-testid="dsl-agent-dsl-input"
        />
      </div>
    </>
  );
};
